import React, { useEffect, useState, useCallback, useRef } from 'react';
import cn from "classnames";
import styles from "./ContractGeneralInfo.module.sass";
import Card from "../../../components/Card";
import TextInput from "../../../components/TextInput";
import Dropdown from "../../../components/Dropdown";
import Switch from "../../../components/Switch";
import Editor from "../../../components/Editor";
import { EditorState, ContentState, convertToRaw, convertFromRaw } from 'draft-js';

const MODE_CONTRACTUEL_OPTIONS = [
  { value: "1", title: "1 - A durée limitée" },
  { value: "2", title: "2 - Dans le cadre d'un CDI" },
  { value: "3", title: "3 - Entreprise de travail temporaire" },
  { value: "4", title: "4 - Activité saisonnière à deux employeurs" }
];


const ContractGeneralInfo = ({ className, data, updateData, currentStep }) => {
  // Récupérer les données du contrat et des tuteurs depuis les props
  const contractInfo = data?.contract?.generalInfo || {};

  // États locaux pour les champs du contrat
  const [modeContractuelType, setModeContractuelType] = useState(() => {
    // Trouver l'option correspondant à la value stockée en BDD
    const storedValue = contractInfo.mode_contractuel_type || '';
    return MODE_CONTRACTUEL_OPTIONS.find(option => option.value === storedValue) || null;
  });
  const [lieuRealisation, setLieuRealisation] = useState(contractInfo.lieu_realisation || '');
  const [intitulePoste, setIntitulePoste] = useState(contractInfo.intitule_poste || '');
  const [machinesDangereuses, setMachinesDangereuses] = useState(contractInfo.machines_dangereuses || false);

  // Gestion de l'éditeur pour la description
  const editorStateRef = useRef(null);
  const isInitializingRef = useRef(false);
  const [content, setContent] = useState(() => {
    const description = data?.contract?.generalInfo?.description_poste || '';
    const editorState = EditorState.createWithContent(ContentState.createFromText(description));
    editorStateRef.current = editorState;
    return editorState;
  });

  // Debounce pour la sauvegarde
  const saveTimeoutRef = useRef(null);
  
  // État pour la validation du formulaire
  const [isFormValid, setIsFormValid] = useState(false);

  // Effet pour initialiser les données du contrat et des tuteurs lorsqu'elles changent
  useEffect(() => {
    if (data?.contract?.generalInfo && !isInitializingRef.current) {
      const contractInfo = data.contract.generalInfo;

      // Mettre à jour les états locaux avec les données du contrat
      const storedValue = contractInfo.mode_contractuel_type || '';
      const selectedOption = MODE_CONTRACTUEL_OPTIONS.find(option => option.value === storedValue) || null;
      setModeContractuelType(selectedOption);
      setLieuRealisation(contractInfo.lieu_realisation || '');
      setIntitulePoste(contractInfo.intitule_poste || '');
      setMachinesDangereuses(contractInfo.machines_dangereuses || false);

      // Mettre à jour l'éditeur de description seulement si le contenu a vraiment changé
      if (contractInfo.description_poste && editorStateRef.current) {
        const currentContent = editorStateRef.current.getCurrentContent().getPlainText();
        let newContent = '';

        try {
          const contentRaw = JSON.parse(contractInfo.description_poste);
          const contentState = convertFromRaw(contentRaw);
          newContent = contentState.getPlainText();
        } catch (error) {
          newContent = contractInfo.description_poste;
        }

        // Ne mettre à jour que si le contenu est différent
        if (currentContent !== newContent) {
          try {
            const contentRaw = JSON.parse(contractInfo.description_poste);
            const contentState = convertFromRaw(contentRaw);
            const newEditorState = EditorState.createWithContent(contentState);
            editorStateRef.current = newEditorState;
            setContent(newEditorState);
          } catch (error) {
            const newEditorState = EditorState.createWithContent(ContentState.createFromText(contractInfo.description_poste));
            editorStateRef.current = newEditorState;
            setContent(newEditorState);
          }
        }
      }
    }
  }, [data?.contract?.generalInfo]);

  // Gestion de l'éditeur pour la description
  const handleEditorChange = useCallback((newEditorState) => {
    isInitializingRef.current = true;
    editorStateRef.current = newEditorState;
    setContent(newEditorState);

    // Debounce la sauvegarde pour éviter trop d'appels
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      const contentState = newEditorState.getCurrentContent();
      const rawContentState = convertToRaw(contentState);
      const richTextContent = JSON.stringify(rawContentState);
      handleInputChange('description_poste', richTextContent);
      isInitializingRef.current = false;
    }, 300);
  }, []);

  const syncDescriptionBeforeSubmit = () => {
    if (editorStateRef.current) {
      // Convertir le contenu de l'éditeur en format JSON pour le texte enrichi
      const contentState = editorStateRef.current.getCurrentContent();
      const rawContentState = convertToRaw(contentState);
      const richTextContent = JSON.stringify(rawContentState);

      // Mettre à jour avec le contenu au format JSON pour le texte enrichi
      handleInputChange('description_poste', richTextContent);
      return richTextContent;
    }
    return '';
  };

  // Fonction de validation du formulaire
  const validateForm = useCallback(() => {
    // Vérifier que le mode contractuel est sélectionné (pas null)
    const isModeContractuelValid = modeContractuelType && modeContractuelType.value;
    
    // Vérifier que l'adresse de réalisation n'est pas vide
    const isLieuRealisationValid = lieuRealisation && lieuRealisation.trim().length > 0;
    
    // Vérifier que l'intitulé du poste n'est pas vide
    const isIntitulePosteValid = intitulePoste && intitulePoste.trim().length > 0;
    
    // Vérifier que la description n'est pas vide
    const currentContent = content.getCurrentContent();
    const isDescriptionValid = currentContent.hasText() && currentContent.getPlainText().trim().length > 0;
    
    const formValid = isModeContractuelValid && isLieuRealisationValid && isIntitulePosteValid && isDescriptionValid;
    
    setIsFormValid(formValid);
    
    // Transmettre l'état de validation au composant parent
    if (updateData) {
      updateData('isValid', formValid);
    }
    
    return formValid;
  }, [modeContractuelType, lieuRealisation, intitulePoste, content, updateData]);

  // Effet pour valider le formulaire à chaque changement
  useEffect(() => {
    validateForm();
  }, [validateForm]);

  // Gestionnaires pour les changements de données
  const handleInputChange = useCallback((name, value) => {
    if (updateData) {
      updateData('generalInfo', { ...data.contract?.generalInfo, [name]: value });
    }
  }, [updateData, data.contract?.generalInfo]);


  return (
    <>
      <Card className={cn(styles.card, className)} title="Informations générales du contrat" classTitle="title-green">

        <div className={styles.field}>
          <Dropdown
            className={styles.dropdown}
            label="Mode contractuel de l'apprentissage"
            name="mode_contractuel_type"
            options={MODE_CONTRACTUEL_OPTIONS}
            setValue={(option) => {
              setModeContractuelType(option);
              // Sauvegarder seulement la value (le code numérique) dans mode_contractuel_type
              handleInputChange('mode_contractuel_type', option.value);
            }}
            value={modeContractuelType}
            placeholder="Veuillez choisir un type de contrat"
            required
          />
        </div>

        <TextInput
          className={styles.field}
          label="Adresse de réalisation du contrat"
          name="lieu_realisation"
          type="text"
          placeholder="Ex: rue, ville, code postal, France"
          value={lieuRealisation}
          onChange={(e) => {
            setLieuRealisation(e.target.value);
            handleInputChange('lieu_realisation', e.target.value);
          }}
          required
        />

      </Card>
      <Card className={cn(styles.card, className)} title="Poste et Mission du contrat" classTitle="title-blue">
        <TextInput
          className={styles.field}
          label="Intitulé du poste"
          name="intitule_poste"
          type="text"
          placeholder="Ex: Développeur Web"
          value={intitulePoste}
          onChange={(e) => {
            setIntitulePoste(e.target.value);
            handleInputChange('intitule_poste', e.target.value);
          }}
          required
        />

        <Editor
          state={content}
          onChange={handleEditorChange}
          classEditor={styles.editor}
          label="Description de la mission"
          tooltip="Description détaillée de la mission et des responsabilités"
          placeholder="Décrivez la mission, les responsabilités et les objectifs..."
        />

        <div className={styles.switchContainer}>
          <span className={styles.switchLabel}>Va-t-il travailler sur des machines dangereuses ?</span>
          <Switch
            name="machines_dangereuses"
            value={machinesDangereuses}
            onChange={(value) => {
              setMachinesDangereuses(value);
              handleInputChange('machines_dangereuses', value);
              // Activer automatiquement is_dangerous_work quand machines_dangereuses est activé
              handleInputChange('is_dangerous_work', value);
            }}
            className={styles.switch}
          />
        </div>

      </Card>

    </>
  );
};


export default ContractGeneralInfo;
